import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';
import { ShareMenu, WriteButton } from '../../common';
import { FriendRequestButton } from '../buttons/FriendRequestButton';
import useStyles from './styles';
import { HeaderProps } from './types';
import StatCards from './StatCards';

function MobileHeader(props: Readonly<HeaderProps>) {
  const { consumer, handleTabChange, activeTab, handleBack } = props;
  const [appBarHeight, setAppBarHeight] = React.useState(0);
  const appBarRef = React.useRef<HTMLElement | null>(null);
  const { account } = useSession();
  const theme = useTheme();
  const styles = useStyles(theme);
  const { t } = useTranslation('cta');

  React.useEffect(() => {
    if (appBarRef.current) {
      setAppBarHeight(appBarRef.current.clientHeight);
    }
  }, []);

  if (!consumer) {
    return null;
  }

  return (
    <Box>
      <AppBar ref={appBarRef} position="fixed" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" onClick={handleBack} sx={{ cursor: 'pointer' }}>
            <theme.icons.back />
            <Typography> {t('back')}</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems={'center'} pr={1}>
            <ShareMenu type="consumer" idOrSlug={consumer.slug} />
          </Stack>
        </Toolbar>
      </AppBar>

      <Stack pt={`calc(${appBarHeight}px + 30px)`} alignItems="center" spacing={3}>
        <Avatar src={consumer.avatar?.url} sx={{ width: 200, height: 200 }} />
        <Stack spacing={1} px={3} alignItems="center">
          <Typography variant="h4" color="textPrimary" textAlign="center">
            {consumer.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            @{consumer.slug}
          </Typography>
        </Stack>
        <StatCards consumer={consumer} />
        {consumer.id !== account?.id && (
          <Stack spacing={1} px={2} width="100%" direction="row">
            <FriendRequestButton consumerId={consumer.id} />
            <WriteButton recipientId={consumer.id} />
          </Stack>
        )}
      </Stack>

      {/* TABS */}
      {consumer.isMyFriend && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="tabs-spot-menu"
          variant="fullWidth"
        >
          <Tab icon={<theme.icons.home sx={{ fontSize: '1.2rem' }} />} sx={styles.tab} />
          <Tab icon={<theme.icons.grid sx={{ fontSize: '1.2rem' }} />} sx={styles.tab} />
          <Tab
            icon={
              <Badge
                color="error"
                badgeContent={consumer.friendships.pending?.list.filter(({ isSent }) => !isSent).length ?? 0}
                max={100}
              >
                <theme.icons.friends sx={{ fontSize: '1.2rem' }} />
              </Badge>
            }
            sx={styles.tab}
          />
        </Tabs>
      )}
    </Box>
  );
}

export default MobileHeader;
